import { apiFetch } from "./client";
import type {
  ComplaintCreatePayload,
  ComplaintDetailResponse,
  ComplaintDraftUpdatePayload,
  ComplaintListQuery,
  ComplaintListResponse,
  ComplaintProposePayload,
  ComplaintResult,
} from "./types";

const BASE = "/Custom/frontend/complaints";

export function fetchComplaints(
  q: ComplaintListQuery = {},
  signal?: AbortSignal,
): Promise<ComplaintListResponse> {
  return apiFetch<ComplaintListResponse>(BASE, {
    query: {
      view: q.view,
      scope: q.scope,
      search: q.search || undefined,
      cursor: q.cursor,
      limit: q.limit,
    },
    signal,
  });
}

export function fetchComplaintDetail(
  id: string,
  signal?: AbortSignal,
): Promise<ComplaintDetailResponse> {
  return apiFetch<ComplaintDetailResponse>(`${BASE}/${encodeURIComponent(id)}`, { signal });
}

export function createComplaint(
  body: ComplaintCreatePayload,
): Promise<ComplaintDetailResponse> {
  return apiFetch<ComplaintDetailResponse>(BASE, { method: "POST", body });
}

/** COMP-S3 W4c — partial draft update (PUT /:id). 409 on stale concurrency token. */
export function updateComplaintDraft(
  id: string,
  body: ComplaintDraftUpdatePayload,
): Promise<ComplaintDetailResponse> {
  return apiFetch<ComplaintDetailResponse>(`${BASE}/${encodeURIComponent(id)}`, {
    method: "PUT",
    body,
  });
}

/** COMP-S3-FIX W4 — author deletes own draft (DELETE /:id). 403 for submitted+/foreign. */
export function deleteComplaint(id: string): Promise<{ id: string; deleted: boolean }> {
  return apiFetch(`${BASE}/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** Generic transition POST — body shape varies per action. */
export function complaintAction(
  id: string,
  action: string,
  body: Record<string, unknown> = {},
): Promise<ComplaintResult> {
  return apiFetch<ComplaintResult>(`${BASE}/${encodeURIComponent(id)}/${action}`, {
    method: "POST",
    body,
  });
}

export function proposeComplaintResolution(
  id: string,
  body: ComplaintProposePayload,
): Promise<ComplaintResult> {
  return complaintAction(id, "propose-resolution", body as unknown as Record<string, unknown>);
}

export function uploadComplaintAttachment(
  id: string,
  body: { fileId: string; attachmentType: string; notes?: string },
): Promise<{ attachmentId: string; attachmentType: string; fileName: string; sha256: string }> {
  return apiFetch(`${BASE}/${encodeURIComponent(id)}/attachments`, {
    method: "POST",
    body,
  });
}

/** Secured streaming download URL (custom endpoint — never the native /Attachment/file). */
export function complaintAttachmentDownloadUrl(complaintId: string, attachmentId: string): string {
  return `/api/v1${BASE}/${encodeURIComponent(complaintId)}/attachments/${encodeURIComponent(
    attachmentId,
  )}/download`;
}
